import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { focusNext } from "../utils/keyboardNav";

/**
 * A single-select dropdown with a search box at the top — for pickers with too many options to scan
 * (customers, products). Options: [{ value, label, sublabel? }]. Behaves like a native <select> from the
 * outside (value + onChange(value)), so it drops into existing forms without changing their state shape.
 *
 * The open panel is rendered into document.body via a portal and positioned with `fixed` coordinates
 * measured from the trigger button, rather than being an absolutely-positioned child of the trigger. This
 * is deliberate: a plain absolutely-positioned child gets silently clipped whenever any ancestor (e.g. a
 * scrollable table wrapper) has overflow other than visible — a portal sidesteps that entirely.
 */
const SearchableSelect = ({ value, onChange, options, placeholder = "Search...", required, style }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState(null);
  // option picked with the arrow keys (index into the filtered options)
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => options.find((o) => String(o.value) === String(value)), [options, value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-searchable-select-panel]")) return;
      setOpen(false);
      setQuery("");
    };
    const reposition = () => {
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: r.width });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.sublabel || "").toLowerCase().includes(q));
  }, [options, query]);

  const openDropdown = (initialQuery = "") => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(true);
    setQuery(initialQuery);
    const at = options.findIndex((o) => String(o.value) === String(value));
    setHighlight(initialQuery ? 0 : Math.max(0, at));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const close = () => {
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  };

  const handleSelect = (option, viaKeyboard = false) => {
    onChange(String(option.value));
    setOpen(false);
    setQuery("");
    // picked with Enter: on to the next field, as Tally does
    if (viaKeyboard && triggerRef.current) {
      const trigger = triggerRef.current;
      requestAnimationFrame(() => {
        trigger.focus();
        focusNext(trigger, 1);
      });
    }
  };

  // closed: Down / Enter / Space or typing opens the list (typing starts the search)
  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDropdown();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      openDropdown(e.key);
    }
  };

  // open: Up / Down move through the matches, Enter picks one, Esc closes, Tab picks the highlighted one when searching
  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((at) => Math.min(Math.max(at + (e.key === "ArrowDown" ? 1 : -1), 0), Math.max(filtered.length - 1, 0)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) handleSelect(filtered[highlight], true);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      if (query && filtered[highlight]) handleSelect(filtered[highlight]);
      else close();
    }
  };

  useEffect(() => {
    if (!open) return;
    document.querySelector(`[data-option-index="${highlight}"]`)?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  return (
    <div ref={containerRef} style={{ position: "relative", ...style }}>
      <button
        ref={triggerRef}
        type="button"
        data-nav-field
        onKeyDown={onTriggerKeyDown}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        style={{
          width: "100%",
          textAlign: "left",
          background: "var(--color-surface, #fff)",
          border: "1px solid var(--color-border, #d7dae1)",
          borderRadius: "8px",
          padding: "8px 10px",
          fontSize: "13px",
          color: selected ? "inherit" : "var(--color-text-faint, #9ca3af)",
          cursor: "pointer",
        }}
      >
        {selected ? selected.label : placeholder}
      </button>
      {required ? (
        <input
          tabIndex={-1}
          value={value || ""}
          onChange={() => {}}
          required
          style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
        />
      ) : null}
      {open && rect
        ? createPortal(
            <div
              data-searchable-select-panel
              style={{
                position: "fixed",
                top: rect.top,
                left: rect.left,
                width: rect.width,
                zIndex: 2000,
                background: "var(--color-surface, #fff)",
                border: "1px solid var(--color-border, #d7dae1)",
                borderRadius: "8px",
                boxShadow: "var(--shadow-md, 0 8px 24px rgba(16,24,40,0.08))",
                maxHeight: "260px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder={placeholder}
                style={{
                  margin: "8px",
                  padding: "6px 8px",
                  border: "1px solid var(--color-border, #d7dae1)",
                  borderRadius: "6px",
                  fontSize: "13px",
                }}
              />
              <div style={{ overflowY: "auto" }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: "10px", fontSize: "13px", color: "var(--color-text-faint, #9ca3af)" }}>No matches</div>
                ) : (
                  filtered.map((option, index) => (
                    <div
                      key={option.value}
                      data-option-index={index}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlight(index)}
                      style={{
                        padding: "8px 10px",
                        fontSize: "13px",
                        cursor: "pointer",
                        background:
                          index === highlight
                            ? "var(--color-primary-soft, #eef2ff)"
                            : String(option.value) === String(value)
                            ? "#f5f6fa"
                            : "transparent",
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {option.label}
                      {option.sublabel ? (
                        <span style={{ color: "var(--color-text-faint, #9ca3af)", marginLeft: "6px" }}>{option.sublabel}</span>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SearchableSelect;
